import { ThemeProvider } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { createAppTheme } from "styles/theme";
import {
  getFromLocalStorage,
  setToLocalStorage,
} from "utils/localStorage";

import { ThemeMode, ThemeModeContext } from "./ThemeModeContext";

const THEME_MODE_KEY = "themeMode";

const getSystemPrefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(
    () => getFromLocalStorage<ThemeMode>(THEME_MODE_KEY) ?? "auto",
  );
  const [systemDark, setSystemDark] = useState(getSystemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    setToLocalStorage(THEME_MODE_KEY, mode);
  }, []);

  const resolvedMode =
    themeMode === "auto" ? (systemDark ? "dark" : "light") : themeMode;

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  useEffect(() => {
    const color = resolvedMode === "dark" ? "#000000" : "#FFFFFF";
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      .forEach((meta) => {
        meta.content = color;
      });
  }, [resolvedMode]);

  return (
    <ThemeModeContext.Provider value={{ themeMode, setThemeMode }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
